import Cookies from 'js-cookie';
import { UAParser } from 'ua-parser-js';

import { ItemData } from '../datasource/generated';
import { IIdentity } from '../stories/App';
import logger from './logger';

export interface ItemDataLayer {
    item_id?: string; // Required GA4 attribute
    item_name?: string; // Required GA4 attribute
    affiliation?: string;
    index?: number;
    item_brand?: string;
    item_category?: string;
    item_category2?: string;
    item_category3?: string;
    item_category4?: string;
    item_category5?: string;
    item_variant?: string;
    location_id?: string;
    quantity?: number;
    coupon?: string;
    discount?: number;
    price?: number;
    item_list_id?: string;
    item_list_name?: string;
    currency?: string;
    // Extra item attributes:  GA4 collection item limitation: up to 27 item-level custom parameters
    item_list_type?: string;
    item_list_price?: number;
    item_location?: string;
    item_practice_area?: string;
    item_practice_area2?: string;
    item_practice_area3?: string;
    item_practice_area4?: string;
    item_practice_area5?: string;
    item_industry?: string;
    item_industry2?: string;
    item_industry3?: string;
    item_industry4?: string;
    item_industry5?: string;
    item_od_segment_source_id?: string;
    item_legacy_brand?: string;
    item_parent_id?: string;
    item_parent_name?: string;
    item_master_id?: string;
    item_master_name?: string;
    item_series_id?: string;
    item_series_name?: string;
}

export function MapItemDataToItemDataLayer(item: ItemData, item_index?: number) {
    try {
        const listId = 'watch';
        const listName = 'watch';
        let listType: string | undefined;
        const index: number | undefined = item_index;
        const itemDataLayer: ItemDataLayer = {
            item_id: item.id || undefined,
            item_name: item.name || undefined,
            affiliation: item.affiliation || undefined,
            index: index,
            item_brand: item.brand || undefined,
            item_category: item.categories ? item.categories[0] : undefined,
            item_category2: item.categories ? item.categories[1] : undefined,
            item_category3: item.categories ? item.categories[2] : undefined,
            item_category4: item.categories ? item.categories[3] : undefined,
            item_category5: item.categories ? item.categories[4] : undefined,
            //item_variant: FFU 
            location_id: item.locationPlaceId || undefined,
            quantity: 1,
            coupon: item.coupon || undefined,
            discount: (item.originalPrice ?? 0) - (item.price ?? 0),
            price: item.originalPrice || undefined,
            item_list_id: listId,
            item_list_name: listName,
            currency: item.currency ?? 'USD',
            // Extra item attributes
            item_list_type: listType,
            item_list_price: item.listPrice || undefined,
            item_location: item.location|| undefined,
            item_practice_area: (item.practiceAreas && item.practiceAreas?.length > 0) ? item.practiceAreas[0] : undefined,
            item_practice_area2: (item.practiceAreas && item.practiceAreas?.length > 1) ? item.practiceAreas[1] : undefined,
            item_practice_area3: (item.practiceAreas && item.practiceAreas?.length > 2) ? item.practiceAreas[2] : undefined,
            item_practice_area4: (item.practiceAreas && item.practiceAreas?.length > 3) ? item.practiceAreas[3] : undefined,
            item_practice_area5: (item.practiceAreas && item.practiceAreas?.length > 4) ? item.practiceAreas[4] : undefined,
            item_industry: (item.industries && item.industries?.length > 0) ? item.industries[0] : undefined,
            item_industry2: (item.industries && item.industries?.length > 1) ? item.industries[1] : undefined,
            item_industry3: (item.industries && item.industries?.length > 2) ? item.industries[2] : undefined,
            item_industry4: (item.industries && item.industries?.length > 3) ? item.industries[3] : undefined,
            item_industry5: (item.industries && item.industries?.length > 4) ? item.industries[4] : undefined,
            item_od_segment_source_id: item.onDemandSegmentSourceItemPk || undefined,
            item_legacy_brand: item.legacyBrand || undefined,
            item_parent_id: item.parentId || undefined,
            item_parent_name: item.parentName || undefined,
            item_master_id: item.masterId || undefined,
            item_master_name: item.masterName || undefined,
            item_series_id: item.seriesId || undefined,
            item_series_name: item.seriesName|| undefined,
        };

        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        return Object.fromEntries(Object.entries(itemDataLayer).filter(([_, v]) => v != null));
    } catch (error) {
        console.warn(error);
        return undefined;
    }
}

const getMemberType = (identity: IIdentity) => {
    if ((identity == null) || (!identity.isAuthorized))
    {
        return 'Anon';
    }
    if (identity.individualSK == 0) {
        return 'Non-Member';
    }
    if (identity.isIM) {
        return 'Individual Member';
    }
    if (identity.isPM) {
        return 'Privileged Member';
    }
    return 'Member';
};

const getFirstVisit = () => {
    const existingCookie = Cookies.get('_pli_first_visit');
    if (existingCookie) {
        try {
            return JSON.parse(existingCookie);
        } catch {
            // malformed cookie: fall through and recreate it
        }
    }

    const firstVisit = {
        date: new Date().toISOString().split('T')[0],
        timestamp: new Date().getTime()
    };
    Cookies.set('_pli_first_visit', JSON.stringify(firstVisit), { expires: 730 }); // 2 years
    return firstVisit;
};

const resolveTrafficSource = () => {
    const params = new URLSearchParams(window.location.search);
    const utmSource = params.get('utm_source');
    const utmMedium = params.get('utm_medium');
    const utmCampaign = params.get('utm_campaign');

    if (utmSource || utmMedium || utmCampaign) {
        return {
            source: utmSource || '',
            medium: utmMedium || '',
            campaign: utmCampaign || ''
        };
    }

    if (document.referrer) {
        try {
            const referrerHostname = new URL(document.referrer).hostname;
            if (referrerHostname !== window.location.hostname) {
                return {
                    source: referrerHostname,
                    medium: 'referral',
                    campaign: ''
                };
            }
        } catch {
            return {
                source: '(direct)',
                medium: '(none)',
                campaign: ''
            };
        }
    }

    return {
        source: '(direct)',
        medium: '(none)',
        campaign: ''
    };
};

const TRAFFIC_SOURCE_STORAGE_KEY = '_pli_traffic_source';

// Session-scoped landing attribution: resolved on the first call of the session and
// persisted, so a later re-send never misattributes to '(direct)' after the UTM params
// are gone from the URL.
const getTrafficSource = () => {
    try {
        const stored = sessionStorage.getItem(TRAFFIC_SOURCE_STORAGE_KEY);
        if (stored) return JSON.parse(stored);
    } catch {
        // ignore and resolve again
    }

    const trafficSource = resolveTrafficSource();

    try {
        sessionStorage.setItem(TRAFFIC_SOURCE_STORAGE_KEY, JSON.stringify(trafficSource));
    } catch {
        // sessionStorage unavailable: still return the resolved value
    }

    return trafficSource;
};

const normalizeStreamId = (measurementId?: string | null) => String(measurementId || '').replace(/^G-/, '');

// GTM's first-party cookie copies can contain the literal string 'deleted'; treat it as absent
const readGaCookie = (name: string) => {
    const value = Cookies.get(name);
    return value && value !== 'deleted' ? value : undefined;
};

/**
 * Client id lives only in the `_ga` (or `_gtm_ga`) cookie: `GA1.1.123456789.1699999999`.
 * GA4's client_id is the LAST TWO segments joined: `123456789.1699999999`.
 */
export const parseGaClientId = (cookieValue: string | undefined): string | null => {
    if (!cookieValue) return null;

    const parts = cookieValue.split('.');
    if (!parts[0]?.startsWith('GA') || parts.length < 4) return null;

    return parts.slice(-2).join('.');
};

/**
 * Session cookie `_ga_<streamId>` holds the session id (an epoch timestamp), not the client id.
 * GS1: `GS1.1.<sessionId>.<sessionNumber>.<engaged>...`
 * GS2: `GS2.1.s<sessionId>$o<sessionNumber>$g...`
 */
export const parseGaSessionCookie = (cookieValue: string | undefined) => {
    if (!cookieValue) {
        return { sessionId: null, sessionNumber: null };
    }

    const parts = cookieValue.split('.');

    if (parts[0] === 'GS2' && parts.length >= 3) {
        const dataString = parts.slice(2).join('.');

        return {
            sessionId: dataString.match(/^s(\d+)/)?.[1] || null,
            sessionNumber: parseInt(dataString.match(/\$o(\d+)/)?.[1] ?? '', 10) || null
        };
    }

    if (parts[0] === 'GS1' && parts.length >= 4) {
        return {
            sessionId: parts[2] || null,
            sessionNumber: parseInt(parts[3], 10) || null
        };
    }

    return { sessionId: null, sessionNumber: null };
};

/** Raw GA session cookie value, preferring the first-party `_gtm_ga*` copies. */
export const getRawGaSessionCookie = (measurementId?: string | null) => {
    const streamId = normalizeStreamId(measurementId);
    return (
        readGaCookie(`_gtm_ga_${streamId}`) ||
        readGaCookie('_gtm_ga') ||
        readGaCookie(`_ga_${streamId}`) ||
        readGaCookie('_ga')
    );
};

/** GA identifiers, joinable 1:1 with GA4 BigQuery (user_pseudo_id / ga_session_id). */
export const getGaIds = (measurementId?: string | null) => {
    const streamId = normalizeStreamId(measurementId);
    const clientCookie = readGaCookie('_gtm_ga') || readGaCookie('_ga');
    const sessionCookie = readGaCookie(`_gtm_ga_${streamId}`) || readGaCookie(`_ga_${streamId}`);
    const { sessionId, sessionNumber } = parseGaSessionCookie(sessionCookie);

    return {
        clientId: parseGaClientId(clientCookie),
        sessionId,
        sessionNumber
    };
};

type NetworkInformationLike = {
    effectiveType?: string;
    downlink?: number;
    rtt?: number;
    saveData?: boolean;
};

export const createSessionContextEvent = (measurementId?: string | null) => {
    const parser = new UAParser(navigator.userAgent);
    const result = parser.getResult();
    const trafficSource = getTrafficSource();
    const firstVisit = getFirstVisit();
    const { clientId, sessionId, sessionNumber } = getGaIds(measurementId);
    const connection = (navigator as Navigator & { connection?: NetworkInformationLike }).connection;
    const deviceMemory = (navigator as Navigator & { deviceMemory?: number }).deviceMemory;

    return {
        event: 'session_context',
        client_id: clientId || 'unknown',
        ga_session_id: sessionId || 'unknown',
        ga_session_number: sessionNumber?.toString() || '1',
        platform: result.os.name ? `${result.os.name} ${result.os.version || ''}`.trim() : 'unknown',
        os: result.os.name || 'unknown',
        os_version: result.os.version || 'unknown',
        browser: result.browser.name || 'unknown',
        browser_version: result.browser.version || 'unknown',
        device_category: result.device.type || 'desktop',
        device_vendor: result.device.vendor || 'unknown',
        device_model: result.device.model || 'unknown',
        user_agent: navigator.userAgent,
        language: navigator.language,
        languages: navigator.languages?.join(',') || navigator.language,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        timezone_offset_min: new Date().getTimezoneOffset(),
        screen_resolution: `${window.screen.width}x${window.screen.height}`,
        viewport_size: `${window.innerWidth}x${window.innerHeight}`,
        pixel_ratio: window.devicePixelRatio,
        color_depth: window.screen.colorDepth,
        device_memory_gb: deviceMemory ?? 0,
        cpu_cores: navigator.hardwareConcurrency || 0,
        touch_support: navigator.maxTouchPoints > 0,
        connection_type: connection?.effectiveType || 'unknown',
        connection_downlink_mbps: connection?.downlink ?? 0,
        connection_rtt_ms: connection?.rtt ?? 0,
        connection_save_data: connection?.saveData ?? false,
        cookies_enabled: navigator.cookieEnabled,
        page_hostname: window.location.hostname,
        landing_page: window.location.pathname + window.location.search,
        traffic_source: trafficSource,
        first_visit_date: firstVisit.date,
        first_visit_timestamp: firstVisit.timestamp.toString(),
        timestamp: new Date().getTime(),
        dataSource: 'Watch'
    };
};

// Titles defined across multiple lines in markup keep the raw newlines/indentation;
// collapse them so page_title is always a single-line value
export const getNormalizedPageTitle = () => document.title.replace(/\s+/g, ' ').trim();

let lastEventTimestamp = Date.now();
let pageViewCount = 0;
let currentPageReferrer: string | null = null;

// For SPA navigations the browser referrer never changes; track the previous virtual
// URL so every event carries the referrer of the page it actually fired on.
const getCurrentPageReferrer = () => currentPageReferrer ?? document.referrer;

// GA4 semantics: engagement_time_msec is the time elapsed since the previous event.
const markEngagementTick = () => {
    const now = Date.now();
    const elapsed = Math.max(0, now - lastEventTimestamp);
    lastEventTimestamp = now;
    return elapsed;
};

const getCampaignParams = (queryString: string) => {
    const params = new URLSearchParams(queryString);
    const campaign: Record<string, string> = {};

    params.forEach((value, key) => {
        if (key.startsWith('utm_') || key === 'gclid' || key === 'gbraid' || key === 'wbraid') {
            campaign[key] = value;
        }
    });

    return campaign;
};

export type SyntheticPageViewParams = {
    uri: string;
    refUri: string;
    queryString: string;
    navigationType: 'spa' | 'pageLoad';
    operationId: string;
    renderTimeMs: number;
    pageLoadTimeMs?: number;
};

export const createSyntheticPageViewEvent = (params: SyntheticPageViewParams) => {
    const pageReferrer =
        params.navigationType === 'spa' && params.refUri ? params.refUri : document.referrer;
    const navTiming = performance.getEntriesByType('navigation')[0] as
        | PerformanceNavigationTiming
        | undefined;

    pageViewCount += 1;
    currentPageReferrer = pageReferrer;

    return {
        is_synthetic: true,
        page_location: `${params.uri}${params.queryString}`,
        page_path: new URL(params.uri).pathname,
        page_hostname: window.location.hostname,
        page_title: getNormalizedPageTitle(),
        page_referrer: pageReferrer,
        navigation_type: params.navigationType,
        ...(params.navigationType === 'pageLoad' && navTiming
            ? { browser_navigation_type: navTiming.type }
            : {}),
        page_view_number: pageViewCount,
        operation_id: params.operationId,
        // Included in the payload (not added by syntheticEventHubEvent) because the pageview
        // reaches eventHubDataLayer via the index.html dataLayer mirror, not a direct push
        engagement_time_msec: markEngagementTick(),
        render_time_msec: Math.round(params.renderTimeMs),
        ...(params.pageLoadTimeMs && params.pageLoadTimeMs > 0
            ? {
                page_load_time_msec: Math.round(params.pageLoadTimeMs),
                ...(navTiming
                    ? {
                        ttfb_msec: Math.round(navTiming.responseStart),
                        dom_content_loaded_msec: Math.round(navTiming.domContentLoadedEventEnd)
                    }
                    : {})
            }
            : {}),
        viewport_size: `${window.innerWidth}x${window.innerHeight}`,
        ...getCampaignParams(params.queryString)
    };
};

export const setUserInfo = (identity: IIdentity) => {
    try {
        window.dataLayer = window.dataLayer || [];
        window.eventHubDataLayer = window.eventHubDataLayer || [];

        window.dataLayer.push({
            event: 'set_user_information',
            userId: identity?.individualSK ? identity.individualSK : null,
            membership: identity?.isPM || identity?.isIM,
            memberType: getMemberType(identity),
            organizationId: identity?.organizationSK ? identity.organizationSK : null,
            source: 'watch.pli.edu'
        });
    } catch (error) {
        logger.error('setUserInfo event error', { error: error,  individualSk: identity?.individualSK, url: window.location.href }, true);
    } 
};

export const getUID = () =>
    String(Date.now().toString(32) + Math.random().toString(16)).replace(
        /\./g,
        '',
    );

// Send an event only to EventHub (synthetic event)
//  Usage. f.ex: syntheticEventHubEvent({ event: 'one' });
//  Only modify the window.eventHubDataLayer array.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const syntheticEventHubEvent = async (event: any) => {
    try {
        window.eventHubDataLayer = window.eventHubDataLayer || [];

        // Page context is captured at push time so each event keeps the page it fired on,
        // even when the user navigates away before the queue is flushed. Caller-provided
        // fields win.
        const newEvent = {
            page_location: window.location.href,
            page_path: window.location.pathname,
            page_hostname: window.location.hostname,
            page_title: getNormalizedPageTitle(),
            page_referrer: getCurrentPageReferrer(),
            engagement_time_msec: markEngagementTick(),
            ...event,
            analytics_event_timestamp: new Date().getTime(),
            'gtm.uniqueEventId': getUID() // synthetic gtm.uniqueEventId
        };

        window.eventHubDataLayer.push(newEvent);
    } catch (error) {
        logger.error('synthetic hub event error', { error: error }, false);
    }
};
